import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAuthUser,
  CustomerUser,
  IUser,
  PartnerUser,
  UserDiscriminator,
  CustomerLoginResponse,
  PartnerLoginResponse,
} from './authTypes';

// Utils
import { saveCurrentVersion, clearStoredVersion } from 'utils/version';

interface AuthState {
  user: IUser | CustomerUser | PartnerUser | null;
  token: string | null;
  isAuthenticated: boolean;
}

const USER_KEY = 'nimax_user';

// Helper function to read from localStorage (internal use only)
const readStoredUser = (): IAuthUser | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || !parsed.user?._id) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

// Initialize state from localStorage
const getInitialAuthState = (): AuthState => {
  const storedUser = readStoredUser();
  
  if (storedUser) {
    return {
      user: storedUser.user,
      token: storedUser.token,
      isAuthenticated: true,
    };
  }

  return {
    user: null,
    token: null,
    isAuthenticated: false,
  };
};

const initialState: AuthState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set authenticated user (from login or onboarding)
     */
    setAuthUser: (state, action: PayloadAction<IAuthUser | CustomerLoginResponse | PartnerLoginResponse>) => {
      if ('customer' in action.payload) {
        // Customer login
        state.user = {
          ...action.payload.customer,
          discriminator: UserDiscriminator.CUSTOMER,
        } as CustomerUser;
        state.token = action.payload.token;
      } else if ('partner' in action.payload) {
        // Partner login
        state.user = {
          ...action.payload.partner,
          discriminator: UserDiscriminator.PARTNER,
        } as PartnerUser;
        state.token = action.payload.token;
      } else {
        // Regular user login
        state.user = {
          ...action.payload.user,
          discriminator: UserDiscriminator.REGULAR,
        } as IUser;
        state.token = action.payload.token;
      }
      state.isAuthenticated = true;

      // Sync with localStorage
      localStorage.setItem(
        USER_KEY,
        JSON.stringify({
          user: state.user,
          token: state.token,
        }),
      );
      localStorage.setItem('nimax_auth_token', state.token || '');

      // Save current app version to detect version changes on next load
      saveCurrentVersion();
    },

    /**
     * Update user data (e.g., after profile update)
     */
    updateAuthUser: (state, action: PayloadAction<Partial<IUser | CustomerUser | PartnerUser>>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        } as IUser | CustomerUser | PartnerUser;

        // Sync with localStorage
        localStorage.setItem(
          USER_KEY,
          JSON.stringify({
            user: state.user,
            token: state.token,
          }),
        );
      }
    },

    /**
     * Clear authentication state (logout)
     */
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;

      // Clear localStorage
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('nimax_auth_token');

      // Clear session expired flag to prevent toast on intentional logout
      localStorage.removeItem('nimax_auth_expired');

      // Clear stored app version
      clearStoredVersion();
    },

    /**
     * Refresh user data from localStorage (useful after external updates)
     */
    refreshAuthFromStorage: (state) => {
      const storedUser = readStoredUser();
      if (storedUser) {
        state.user = storedUser.user;
        state.token = storedUser.token;
        state.isAuthenticated = true;
      } else {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      }
    },
  },
});

export const { setAuthUser, updateAuthUser, clearAuth, refreshAuthFromStorage } =
  authSlice.actions;

// Selectors
export const selectAuthUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAdminUser = (state: { auth: AuthState }): IUser | null => {
  const user = state.auth.user;
  if (user && user.discriminator === UserDiscriminator.REGULAR) {
    return user as IUser;
  }
  return null;
};
export const selectCustomerUser = (state: { auth: AuthState }): CustomerUser | null => {
  const user = state.auth.user;
  if (user && user.discriminator === UserDiscriminator.CUSTOMER) {
    return user as CustomerUser;
  }
  return null;
};
export const selectPartnerUser = (state: { auth: AuthState }): PartnerUser | null => {
  const user = state.auth.user;
  if (user && user.discriminator === UserDiscriminator.PARTNER) {
    return user as PartnerUser;
  }
  return null;
};
export const selectUserCompany = (state: { auth: AuthState }): string | null => {
  const user = state.auth.user;
  // Only IUser has company property, CustomerUser doesn't
  if (user && 'company' in user && user.company) {
    // IUser.company is always a string, so we can safely return it
    return user.company as string;
  }
  return null;
};

export default authSlice.reducer;

